import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { FiSearch, FiX, FiStar, FiClock, FiArrowRight } from "react-icons/fi";

const STORAGE_KEY = "armRecentSearches";

const SEARCH_ITEMS = [
  { title: "Dashboard", group: "Overview", path: "/dashboard", keywords: "home overview balance quick actions" },
  { title: "Mobile Wallet", group: "Wallet", path: "/wallet", keywords: "wallet balance transaction setting mobile currency" },
  { title: "Bank Account", group: "Accounts", path: "/my-account", keywords: "bank account linked accounts net worth" },
  { title: "Send Money", group: "Payments", path: "/new-transfer", keywords: "transfer payment existing recipient someone new" },
  { title: "Pending Transactions", group: "Payments", path: "/pending-transactions", keywords: "pending scheduled transfers cancel" },
  { title: "Recipients", group: "Beneficiary", path: "/recipient", keywords: "beneficiary recipient directory edit delete export" },
  { title: "Add Recipient", group: "Beneficiary", path: "/add-recipient", keywords: "new beneficiary recipient bank wallet" },
  { title: "Convert", group: "Wallet", path: "/convert", keywords: "currency swap conversion exchange rate" },
  { title: "Add Funds", group: "Wallet", path: "/add-funds", keywords: "topup deposit direct debit card" },
  { title: "Budget Setup", group: "Manage Spending", path: "/budget-setup", keywords: "budget limits categories spending" },
  { title: "Create Budget", group: "Manage Spending", path: "/create-budget", keywords: "new budget amount period monthly yearly" },
  { title: "Budget Tracking", group: "Manage Spending", path: "/budget-tracking", keywords: "budget progress spending tracker" },
  { title: "Budget Report", group: "Reports", path: "/budget-report", keywords: "budget report tax cashflow" },
  { title: "Spending Report", group: "Reports", path: "/spending-report", keywords: "spending report income expense categories" },
  { title: "Income Expense Calendar", group: "Manage Spending", path: "/income-expense-calendar", keywords: "calendar income expense month" },
  { title: "My Bills", group: "Manage Spending", path: "/my-bills", keywords: "bills billers payments calendar" },
  { title: "Statements", group: "Reports", path: "/statement", keywords: "statement invoices reports export" },
  { title: "Reports", group: "Reports", path: "/reports", keywords: "reports statement budget spending" },
  { title: "Invoice Generator", group: "Reports", path: "/invoice-generator", keywords: "invoice generator create bill client tax purchase order" },
  { title: "Request Money", group: "Payments", path: "/request-money", keywords: "request payment link qr" },
  { title: "QR Payment", group: "Payments", path: "/qr-payment", keywords: "qr code receive pay" },
  { title: "Cards", group: "Cards", path: "/card", keywords: "card settings company employee" },
  { title: "Settings", group: "Profile", path: "/settings", keywords: "profile kyc security account settings" },
  { title: "Help Center", group: "Support", path: "/help-center", keywords: "support help faq contact" },
];

const readRecent = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    return Array.isArray(parsed) ? parsed.slice(0, 6) : [];
  } catch {
    return [];
  }
};

const GlobalSearch = () => {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [recent, setRecent] = useState(readRecent);

  useEffect(() => {
    const onKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setOpen(true);
      }
      if (event.key === "Escape") {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 40);
    } else {
      setQuery("");
    }
  }, [open]);

  const results = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return recent.length ? recent : SEARCH_ITEMS.slice(0, 8);
    return SEARCH_ITEMS.filter((item) =>
      `${item.title} ${item.group} ${item.keywords}`.toLowerCase().includes(term)
    ).slice(0, 10);
  }, [query, recent]);

  const saveRecent = (item) => {
    const next = [item, ...recent.filter((entry) => entry.path !== item.path)].slice(0, 6);
    setRecent(next);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  };

  const choose = (item) => {
    saveRecent(item);
    setOpen(false);
    navigate(item.path);
  };

  const removeRecent = (path, event) => {
    event.stopPropagation();
    const next = recent.filter((item) => item.path !== path);
    setRecent(next);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  };

  const showingRecent = !query.trim() && recent.length > 0;

  return (
    <>
      <button
        type="button"
        className="navbar-search-trigger"
        onClick={() => setOpen(true)}
        aria-label="Open search"
      >
        <FiSearch size={16} />
        <span>Ctrl K</span>
      </button>

      {open && createPortal(
        <div className="global-search-overlay" onMouseDown={() => setOpen(false)}>
          <div className="global-search-modal" onMouseDown={(event) => event.stopPropagation()}>
            <div className="global-search-input-row">
              <FiSearch size={20} />
              <input
                ref={inputRef}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search modules, features, pages"
              />
              <button type="button" onClick={() => setOpen(false)}>esc</button>
            </div>

            <div className="global-search-body">
              <div className="global-search-section-title">
                {showingRecent ? "Recent" : query.trim() ? "Results" : "Suggested"}
              </div>

              {results.length === 0 ? (
                <div className="global-search-empty">No matching modules found</div>
              ) : (
                results.map((item) => (
                  <div
                    key={item.path}
                    className="global-search-result"
                    role="button"
                    tabIndex={0}
                    onClick={() => choose(item)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        choose(item);
                      }
                    }}
                  >
                    <div className="global-search-result-main">
                      <span className="global-search-result-icon">
                        {showingRecent ? <FiClock size={16} /> : <FiSearch size={16} />}
                      </span>
                      <div>
                        <span className="global-search-result-group">{item.group}</span>
                        <strong>{item.title}</strong>
                      </div>
                    </div>
                    <div className="global-search-result-actions">
                      <FiStar size={17} />
                      {showingRecent ? (
                        <button
                          type="button"
                          className="global-search-remove"
                          onClick={(event) => removeRecent(item.path, event)}
                          aria-label={`Remove ${item.title} from recent searches`}
                        >
                          <FiX size={18} />
                        </button>
                      ) : (
                        <FiArrowRight size={17} />
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default GlobalSearch;




